/**
 * Сохранённые виды графика (ПЛ-29): выбрать свой или общий вид команды, сохранить текущий, удалить.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { toast } from '../../ui/feedback';
import { Checkbox, Input, Select } from '../../ui/form';
import { LField } from './LField';

interface SavedView {
  id: string;
  name: string;
  shared: boolean;
  mine: boolean;
  state: Record<string, unknown>;
}

export function ViewsMenu({
  scheduleId,
  canShare,
  current,
  onApply,
}: {
  scheduleId: string;
  canShare: boolean;
  current: Record<string, unknown>;
  onApply: (state: Record<string, unknown>) => void;
}) {
  const qc = useQueryClient();
  const key = ['schedule-views', scheduleId];
  const q = useQuery({ queryKey: key, queryFn: () => api<SavedView[]>(`/schedules/${scheduleId}/views`) });
  const [picked, setPicked] = useState('');
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: '', shared: false });
  const save = useMutation({
    mutationFn: () =>
      api<SavedView>(`/schedules/${scheduleId}/views`, {
        body: { name: f.name.trim(), shared: f.shared, state: current },
      }),
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: key });
      setPicked(v.id);
      setSaving(false);
      toast(`Вид «${v.name}» сохранён`, 'success');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/schedule/views/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      setPicked('');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const list = q.data ?? [];
  const sel = list.find((v) => v.id === picked);
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span className="w-[calc(100vw-120px)] sm:w-[200px]">
        <Select
          value={picked}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '+') {
              setF({ name: '', shared: false });
              setSaving(true);
              return;
            }
            setPicked(v);
            const x = list.find((y) => y.id === v);
            if (x) onApply(x.state);
          }}
          className="max-md:h-11"
          aria-label="Сохранённые виды"
        >
          <option value="">Сохранённые виды</option>
          {list.filter((v) => !v.shared).length ? (
            <optgroup label="Мои виды">
              {list
                .filter((v) => !v.shared)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
            </optgroup>
          ) : null}
          {list.filter((v) => v.shared).length ? (
            <optgroup label="Общие виды команды">
              {list
                .filter((v) => v.shared)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
            </optgroup>
          ) : null}
          <option value="+">Сохранить текущий вид…</option>
        </Select>
      </span>
      {sel && (sel.mine || (sel.shared && canShare)) ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => window.confirm(`Удалить вид «${sel.name}»?`) && remove.mutate(sel.id)}
          className="max-md:h-11"
        >
          Удалить вид
        </Button>
      ) : null}
      {saving ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setSaving(false)}
          title="Сохранить вид графика"
          description="Шкала, отбор, группировка, сортировка и вид — диаграмма Ганта или сетевой график."
          width="sm"
          footer={
            <>
              <Button onClick={() => setSaving(false)} className="max-md:h-11">
                Отмена
              </Button>
              <Button
                variant="primary"
                disabled={!f.name.trim()}
                loading={save.isPending}
                onClick={() => save.mutate()}
                className="max-md:h-11"
              >
                Сохранить
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <LField label="Название">
              <Input
                value={f.name}
                maxLength={80}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
            {canShare ? (
              <Checkbox
                className="min-h-11 items-center md:min-h-0"
                label="Общий вид для всей команды проекта"
                checked={f.shared}
                onChange={(e) => setF({ ...f, shared: e.target.checked })}
              />
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </span>
  );
}
